/**
 * MODULE: _shared/lib/frontend/api/gatewayRateLimit.ts
 * PURPOSE: Shared gateway **HTTP 429** cooldown (**Retry-After** + JSON **`retry_after`**) so multiple shell clients (**fetch**, **axios**) skip outbound calls in the same window and DevTools stops repeating **Failed to load resource**.
 * DEPENDENCIES: axios (**isAxiosError**) for **`bumpGateway429FromAxiosError`**
 * PUBLIC_API: isInGatewayBackoff, clearGatewayBackoff, bumpGateway429FromResponse, bumpGateway429FromAxiosError, GATEWAY_BACKOFF_SS_KEY
 * SIDE_EFFECTS: **sessionStorage**, module-level deadline
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 80 / 150 lines
 * RECENT: 2026-05-08 — **`_shared/frontend/lib/frontend/api/gatewayRateLimit.ts`** shim re-exports this file for Vite **`@shared`** alias (**`CHANGELOG` [2.2.874]**).
 */

import axios from 'axios';

export const GATEWAY_BACKOFF_SS_KEY = 'brazn_gateway_api_backoff_until';

const MIN_MS = 60_000;
const MAX_MS = 15 * 60 * 1000;
const DEFAULT_MS = 120_000;

let gatewayBackoffUntil = 0;

function readStored(): number {
  if (typeof sessionStorage === 'undefined') return 0;
  try {
    return parseInt(sessionStorage.getItem(GATEWAY_BACKOFF_SS_KEY) || '0', 10);
  } catch {
    return 0;
  }
}

function effectiveUntil(): number {
  return Math.max(gatewayBackoffUntil, readStored());
}

export function isInGatewayBackoff(): boolean {
  return Date.now() < effectiveUntil();
}

/** Clear armed 429 cooldown (e.g. after a successful OAuth callback so status queries run again). */
export function clearGatewayBackoff(): void {
  gatewayBackoffUntil = 0;
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(GATEWAY_BACKOFF_SS_KEY);
    }
  } catch {
    /* private mode */
  }
}

function deltaMsFromRetryAfterHeader(raw: string | string[] | undefined): number | null {
  const s = Array.isArray(raw) ? raw[0] : raw;
  if (typeof s !== 'string' || s === '') return null;
  const sec = parseInt(s, 10);
  if (Number.isNaN(sec) || sec < 0) return null;
  return Math.min(Math.max(sec * 1000, MIN_MS), MAX_MS);
}

function deltaMsFromJsonBody(data: unknown): number | null {
  if (!data || typeof data !== 'object' || !('retry_after' in data)) return null;
  const n = (data as { retry_after?: unknown }).retry_after;
  if (typeof n !== 'number' || n < 0) return null;
  return Math.min(Math.max(n * 1000, MIN_MS), MAX_MS);
}

function armBackoffMs(deltaMs: number): void {
  const proposedUntil = Date.now() + deltaMs;
  gatewayBackoffUntil = Math.max(gatewayBackoffUntil, proposedUntil);
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(GATEWAY_BACKOFF_SS_KEY, String(gatewayBackoffUntil));
    }
  } catch {
    /* private mode */
  }
}

/** Arm from a **`fetch`** **Response** (reads **`Retry-After`** header). */
export function bumpGateway429FromResponse(response: Response): void {
  if (response.status !== 429) return;
  const fromHeader = deltaMsFromRetryAfterHeader(response.headers.get('retry-after') ?? undefined);
  armBackoffMs(fromHeader ?? DEFAULT_MS);
}

/** Arm from an **Axios** error after **HTTP 429** (headers + optional **`retry_after`** in JSON body). */
export function bumpGateway429FromAxiosError(err: unknown): void {
  if (!axios.isAxiosError(err) || err.response?.status !== 429) return;
  const h = err.response.headers as Record<string, string | string[] | undefined>;
  const fromHeader =
    deltaMsFromRetryAfterHeader(h['retry-after']) ?? deltaMsFromRetryAfterHeader(h['Retry-After']);
  const fromBody = deltaMsFromJsonBody(err.response.data);
  armBackoffMs(fromHeader ?? fromBody ?? DEFAULT_MS);
}
